import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiCreditCard,
    FiPercent,
    FiRefreshCw,
    FiTrendingUp,
} from "react-icons/fi";

import "./InvoiceTotals.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const EMPTY_INVOICE_TOTALS = {
    itemGrossAmount: 0,
    itemDiscountAmount: 0,
    subtotalAmount: 0,

    invoiceDiscountType: "NONE",
    invoiceDiscountValue: 0,
    invoiceDiscountAmount: 0,

    taxableAmount: 0,

    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    cessAmount: 0,
    totalTaxAmount: 0,

    shippingCharge: 0,
    packingCharge: 0,
    otherCharge: 0,

    roundOffEnabled: true,
    roundOffAmount: 0,

    grandTotal: 0,

    paidAmount: 0,
    balanceAmount: 0,

    paymentStatus: "UNPAID",
};

const DISCOUNT_TYPES = [
    {
        value: "NONE",
        label: "No Discount",
    },
    {
        value: "PERCENTAGE",
        label: "Percentage",
    },
    {
        value: "FLAT",
        label: "Flat Amount",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(
        parsed
    )
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) => {
    return Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;
};

const clamp = (
    value,
    minimum,
    maximum
) => {
    return Math.min(
        Math.max(
            value,
            minimum
        ),
        maximum
    );
};

const formatMoney = (
    amount
) => {
    return `₹${roundMoney(
        amount
    ).toLocaleString(
        "en-IN",
        {
            minimumFractionDigits:
                2,
            maximumFractionDigits:
                2,
        }
    )}`;
};

const getPaymentStatus = ({
    grandTotal,
    paidAmount,
}) => {
    const total =
        roundMoney(
            grandTotal
        );

    const paid =
        roundMoney(
            paidAmount
        );

    if (
        total <= 0
    ) {
        return "NOT_APPLICABLE";
    }

    if (
        paid <= 0
    ) {
        return "UNPAID";
    }

    if (
        paid >= total
    ) {
        return "PAID";
    }

    return "PARTIALLY_PAID";
};

const calculateInvoiceTotals = ({
    itemTotals = {},
    invoiceDiscountType =
        "NONE",
    invoiceDiscountValue = 0,
    shippingCharge = 0,
    packingCharge = 0,
    otherCharge = 0,
    roundOffEnabled = true,
    paidAmount = 0,
}) => {
    const itemGrossAmount =
        roundMoney(
            itemTotals
                ?.grossAmount ||
            0
        );

    const itemDiscountAmount =
        roundMoney(
            itemTotals
                ?.discountAmount ||
            0
        );

    const subtotalAmount =
        roundMoney(
            itemTotals
                ?.taxableAmount ||
            Math.max(
                itemGrossAmount -
                    itemDiscountAmount,
                0
            )
        );

    const normalizedDiscountType =
        [
            "NONE",
            "PERCENTAGE",
            "FLAT",
        ].includes(
            invoiceDiscountType
        )
            ? invoiceDiscountType
            : "NONE";

    const normalizedDiscountValue =
        Math.max(
            toNumber(
                invoiceDiscountValue
            ),
            0
        );

    let invoiceDiscountAmount = 0;

    if (
        normalizedDiscountType ===
        "PERCENTAGE"
    ) {
        invoiceDiscountAmount =
            roundMoney(
                subtotalAmount *
                    clamp(
                        normalizedDiscountValue,
                        0,
                        100
                    ) /
                    100
            );
    } else if (
        normalizedDiscountType ===
        "FLAT"
    ) {
        invoiceDiscountAmount =
            Math.min(
                roundMoney(
                    normalizedDiscountValue
                ),
                subtotalAmount
            );
    }

    const taxableAmount =
        roundMoney(
            Math.max(
                subtotalAmount -
                    invoiceDiscountAmount,
                0
            )
        );

    const itemTaxableAmount =
        roundMoney(
            itemTotals
                ?.taxableAmount ||
            0
        );

    const taxScale =
        itemTaxableAmount >
        0
            ? taxableAmount /
                itemTaxableAmount
            : 0;

    const cgstAmount =
        roundMoney(
            (
                itemTotals
                    ?.cgstAmount ||
                0
            ) *
                taxScale
        );

    const sgstAmount =
        roundMoney(
            (
                itemTotals
                    ?.sgstAmount ||
                0
            ) *
                taxScale
        );

    const igstAmount =
        roundMoney(
            (
                itemTotals
                    ?.igstAmount ||
                0
            ) *
                taxScale
        );

    const cessAmount =
        roundMoney(
            (
                itemTotals
                    ?.cessAmount ||
                0
            ) *
                taxScale
        );

    const totalTaxAmount =
        roundMoney(
            cgstAmount +
                sgstAmount +
                igstAmount +
                cessAmount
        );

    const normalizedShippingCharge =
        Math.max(
            roundMoney(
                shippingCharge
            ),
            0
        );

    const normalizedPackingCharge =
        Math.max(
            roundMoney(
                packingCharge
            ),
            0
        );

    const normalizedOtherCharge =
        Math.max(
            roundMoney(
                otherCharge
            ),
            0
        );

    const totalBeforeRoundOff =
        roundMoney(
            taxableAmount +
                totalTaxAmount +
                normalizedShippingCharge +
                normalizedPackingCharge +
                normalizedOtherCharge
        );

    const roundedGrandTotal =
        roundOffEnabled
            ? Math.round(
                totalBeforeRoundOff
            )
            : totalBeforeRoundOff;

    const roundOffAmount =
        roundMoney(
            roundedGrandTotal -
                totalBeforeRoundOff
        );

    const grandTotal =
        roundMoney(
            roundedGrandTotal
        );

    const normalizedPaidAmount =
        clamp(
            roundMoney(
                paidAmount
            ),
            0,
            grandTotal
        );

    const balanceAmount =
        roundMoney(
            Math.max(
                grandTotal -
                    normalizedPaidAmount,
                0
            )
        );

    return {
        itemGrossAmount,
        itemDiscountAmount,
        subtotalAmount,

        invoiceDiscountType:
            normalizedDiscountType,

        invoiceDiscountValue:
            normalizedDiscountValue,

        invoiceDiscountAmount,

        taxableAmount,

        cgstAmount,
        sgstAmount,
        igstAmount,
        cessAmount,
        totalTaxAmount,

        shippingCharge:
            normalizedShippingCharge,

        packingCharge:
            normalizedPackingCharge,

        otherCharge:
            normalizedOtherCharge,

        roundOffEnabled:
            Boolean(
                roundOffEnabled
            ),

        roundOffAmount,

        grandTotal,

        paidAmount:
            normalizedPaidAmount,

        balanceAmount,

        paymentStatus:
            getPaymentStatus({
                grandTotal,
                paidAmount:
                    normalizedPaidAmount,
            }),
    };
};

const validateInvoiceTotals = (
    totals
) => {
    const errors = {};

    if (
        totals
            .invoiceDiscountType ===
            "PERCENTAGE" &&
        (
            totals
                .invoiceDiscountValue <
                0 ||
            totals
                .invoiceDiscountValue >
                100
        )
    ) {
        errors.invoiceDiscountValue =
            "Discount percentage must be between 0 and 100.";
    }

    if (
        totals
            .invoiceDiscountAmount >
        totals.subtotalAmount
    ) {
        errors.invoiceDiscountValue =
            "Invoice discount cannot exceed subtotal.";
    }

    if (
        totals.shippingCharge <
        0
    ) {
        errors.shippingCharge =
            "Shipping charge cannot be negative.";
    }

    if (
        totals.packingCharge <
        0
    ) {
        errors.packingCharge =
            "Packing charge cannot be negative.";
    }

    if (
        totals.otherCharge <
        0
    ) {
        errors.otherCharge =
            "Other charge cannot be negative.";
    }

    if (
        totals.paidAmount >
        totals.grandTotal
    ) {
        errors.paidAmount =
            "Paid amount cannot exceed grand total.";
    }

    return errors;
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const InvoiceTotals = ({
    itemTotals = {},
    value,
    onChange,
    onValidationChange,
    disabled = false,
    documentType =
        "SALE_INVOICE",
    showPayment = true,
    showCharges = true,
    allowRoundOff = true,
    showTaxBreakup = true,
    defaultFullPayment = true,
}) => {
    const source =
        useMemo(
            () => ({
                ...EMPTY_INVOICE_TOTALS,
                ...(value || {}),
            }),
            [value]
        );

    /*
    |--------------------------------------------------------------------------
    | QUICK PAYMENT PRESET
    |--------------------------------------------------------------------------
    |
    | Create invoice can start with FULL payment selected.
    | Edit invoice passes defaultFullPayment={false}, so old payment data is not
    | overwritten.
    |
    |--------------------------------------------------------------------------
    */

    const [
        quickPaymentPreset,
        setQuickPaymentPreset,
    ] =
        useState(
            defaultFullPayment
                ? "FULL"
                : ""
        );


    const calculated =
        useMemo(
            () =>
                calculateInvoiceTotals({
                    itemTotals,

                    invoiceDiscountType:
                        source
                            .invoiceDiscountType,

                    invoiceDiscountValue:
                        source
                            .invoiceDiscountValue,

                    shippingCharge:
                        source
                            .shippingCharge,

                    packingCharge:
                        source
                            .packingCharge,

                    otherCharge:
                        source
                            .otherCharge,

                    roundOffEnabled:
                        allowRoundOff
                            ? source
                                .roundOffEnabled !==
                            false
                            : false,

                    paidAmount:
                        showPayment
                            ? source
                                .paidAmount
                            : 0,
                }),
            [
                allowRoundOff,
                itemTotals,
                showPayment,
                source
                    .invoiceDiscountType,
                source
                    .invoiceDiscountValue,
                source
                    .otherCharge,
                source
                    .packingCharge,
                source
                    .paidAmount,
                source
                    .roundOffEnabled,
                source
                    .shippingCharge,
            ]
        );

    const errors =
        useMemo(
            () =>
                validateInvoiceTotals(
                    calculated
                ),
            [calculated]
        );

    useEffect(() => {
        if (
            typeof onValidationChange ===
            "function"
        ) {
            onValidationChange({
                valid:
                    Object.keys(
                        errors
                    ).length ===
                    0,

                errors,

                totals:
                    calculated,
            });
        }
    }, [
        calculated,
        errors,
        onValidationChange,
    ]);

    useEffect(() => {
        if (
            typeof onChange !==
            "function"
        ) {
            return;
        }

        const changed =
            Object.keys(
                calculated
            ).some(
                (
                    key
                ) =>
                    calculated[
                        key
                    ] !==
                    source[key]
            );

        if (
            changed
        ) {
            onChange({
                ...source,
                ...calculated,
            });
        }
    }, [
        calculated,
        onChange,
        source,
    ]);

    const handleFieldChange =
        useCallback(
            (
                field,
                nextValue
            ) => {
                onChange?.({
                    ...source,
                    [field]:
                        nextValue,
                });
            },
            [
                onChange,
                source,
            ]
        );

    const resetInvoiceAdjustments =
        useCallback(() => {
            onChange?.({
                ...source,

                invoiceDiscountType:
                    "NONE",

                invoiceDiscountValue:
                    0,

                shippingCharge:
                    0,

                packingCharge:
                    0,

                otherCharge:
                    0,

                roundOffEnabled:
                    true,

                paidAmount:
                    0,
            });
        }, [
            onChange,
            source,
        ]);

    const isQuotation =
        documentType ===
        "QUOTATION";


    /*
    |--------------------------------------------------------------------------
    | APPLY ACTIVE QUICK PAYMENT PRESET
    |--------------------------------------------------------------------------
    |
    | When total changes because of items, discount, tax or charges, keep the
    | selected preset synchronized.
    |
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            if (
                !showPayment ||
                isQuotation ||
                !quickPaymentPreset
            ) {
                return;
            }


            const percentages = {
                FULL:
                    100,

                HALF:
                    50,

                QUARTER:
                    25,
            };


            const percentage =
                percentages[
                    quickPaymentPreset
                ];


            if (
                !percentage
            ) {
                return;
            }


            const nextPaidAmount =
                roundMoney(
                    calculated
                        .grandTotal *
                    (
                        percentage /
                        100
                    )
                );


            if (
                roundMoney(
                    calculated
                        .paidAmount
                ) ===
                nextPaidAmount
            ) {
                return;
            }


            handleFieldChange(
                "paidAmount",
                nextPaidAmount
            );

        },
        [
            calculated
                .grandTotal,
            calculated
                .paidAmount,
            handleFieldChange,
            isQuotation,
            quickPaymentPreset,
            showPayment,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | QUICK PAYMENT CLICK
    |--------------------------------------------------------------------------
    |
    | Clicking the already-selected option clears payment.
    | Example:
    | Full Balance -> selected
    | Full Balance again -> unselected + Paid Amount = 0
    |
    |--------------------------------------------------------------------------
    */

    const handleQuickPayment =
        useCallback(
            (
                preset,
                percentage
            ) => {

                if (
                    disabled
                ) {
                    return;
                }


                if (
                    quickPaymentPreset ===
                    preset
                ) {

                    setQuickPaymentPreset(
                        ""
                    );


                    handleFieldChange(
                        "paidAmount",
                        0
                    );


                    return;
                }


                setQuickPaymentPreset(
                    preset
                );


                const nextPaidAmount =
                    roundMoney(
                        calculated
                            .grandTotal *
                        (
                            percentage /
                            100
                        )
                    );


                handleFieldChange(
                    "paidAmount",
                    nextPaidAmount
                );

            },
            [
                calculated
                    .grandTotal,
                disabled,
                handleFieldChange,
                quickPaymentPreset,
            ]
        );


    const handleManualPaidAmountChange =
        useCallback(
            (
                event
            ) => {

                setQuickPaymentPreset(
                    ""
                );


                handleFieldChange(
                    "paidAmount",
                    event.target
                        .value
                );

            },
            [
                handleFieldChange,
            ]
        );


    return (
        <section className="invoice-totals-section">
            <div className="invoice-totals-header">
                <div className="invoice-totals-title">
                    <span className="invoice-totals-icon">
                        <FiTrendingUp />
                    </span>

                    <div>
                        <h3>
                            Invoice Summary
                        </h3>

                        <p>
                            Review discounts, taxes, charges, payment and final balance.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className="invoice-totals-reset-button"
                    disabled={
                        disabled
                    }
                    onClick={
                        resetInvoiceAdjustments
                    }
                >
                    <FiRefreshCw />

                    Reset Adjustments
                </button>
            </div>

            <div className="invoice-totals-layout">
                <div className="invoice-totals-controls">
                    <div className="invoice-totals-control-card">
                        <div className="invoice-totals-control-title">
                            <FiPercent />

                            <div>
                                <strong>
                                    Invoice Discount
                                </strong>

                                <span>
                                    Applied after item-level discounts.
                                </span>
                            </div>
                        </div>

                        <div className="invoice-totals-grid invoice-totals-grid-two">
                            <div className="invoice-totals-field">
                                <label>
                                    Discount Type
                                </label>

                                <select
                                    value={
                                        calculated
                                            .invoiceDiscountType
                                    }
                                    disabled={
                                        disabled
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleFieldChange(
                                            "invoiceDiscountType",
                                            event.target
                                                .value
                                        )
                                    }
                                >
                                    {DISCOUNT_TYPES.map(
                                        (
                                            option
                                        ) => (
                                            <option
                                                key={
                                                    option.value
                                                }
                                                value={
                                                    option.value
                                                }
                                            >
                                                {option.label}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>

                            <div className="invoice-totals-field">
                                <label>
                                    Discount Value
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={
                                        calculated
                                            .invoiceDiscountValue
                                    }
                                    disabled={
                                        disabled ||
                                        calculated
                                            .invoiceDiscountType ===
                                            "NONE"
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleFieldChange(
                                            "invoiceDiscountValue",
                                            event.target
                                                .value
                                        )
                                    }
                                />

                                {errors
                                    .invoiceDiscountValue && (
                                    <small className="invoice-totals-field-error">
                                        {errors
                                            .invoiceDiscountValue}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    {showCharges && (
                        <div className="invoice-totals-control-card">
                            <div className="invoice-totals-control-title">
                                <FiCreditCard />

                                <div>
                                    <strong>
                                        Additional Charges
                                    </strong>

                                    <span>
                                        Add shipping, packing or other invoice charges.
                                    </span>
                                </div>
                            </div>

                            <div className="invoice-totals-grid invoice-totals-grid-three">
                                <div className="invoice-totals-field">
                                    <label>
                                        Shipping
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={
                                            calculated
                                                .shippingCharge
                                        }
                                        disabled={
                                            disabled
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleFieldChange(
                                                "shippingCharge",
                                                event.target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="invoice-totals-field">
                                    <label>
                                        Packing
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={
                                            calculated
                                                .packingCharge
                                        }
                                        disabled={
                                            disabled
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleFieldChange(
                                                "packingCharge",
                                                event.target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="invoice-totals-field">
                                    <label>
                                        Other
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={
                                            calculated
                                                .otherCharge
                                        }
                                        disabled={
                                            disabled
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleFieldChange(
                                                "otherCharge",
                                                event.target
                                                    .value
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {allowRoundOff && (
                        <label className="invoice-totals-roundoff-toggle">
                            <input
                                type="checkbox"
                                checked={
                                    calculated
                                        .roundOffEnabled
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "roundOffEnabled",
                                        event.target
                                            .checked
                                    )
                                }
                            />

                            <span>
                                Round final amount to nearest rupee
                            </span>
                        </label>
                    )}

                    {showPayment &&
                        !isQuotation && (
                        <div className="invoice-totals-payment-card">
                            <div className="invoice-totals-payment-header">
                                <div>
                                    <strong>
                                        Payment Received
                                    </strong>

                                    <span>
                                        Optional payment while creating the invoice.
                                    </span>
                                </div>

                                <span
                                    className={`invoice-totals-payment-status invoice-totals-payment-${calculated.paymentStatus.toLowerCase()}`}
                                >
                                    {calculated.paymentStatus.replace(
                                        /_/g,
                                        " "
                                    )}
                                </span>
                            </div>

                            <div className="invoice-totals-field">
                                <label>
                                    Paid Amount
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    max={
                                        calculated
                                            .grandTotal
                                    }
                                    step="0.01"
                                    value={
                                        calculated
                                            .paidAmount
                                    }
                                    disabled={
                                        disabled
                                    }
                                    onChange={
                                        handleManualPaidAmountChange
                                    }
                                />

                                {errors
                                    .paidAmount && (
                                    <small className="invoice-totals-field-error">
                                        {errors
                                            .paidAmount}
                                    </small>
                                )}


                                <div className="invoice-totals-quick-payment">

                                    <span className="invoice-totals-quick-payment-label">
                                        Quick Payment
                                    </span>


                                    <div className="invoice-totals-quick-payment-buttons">

                                        <button
                                            type="button"

                                            className={
                                                quickPaymentPreset ===
                                                "FULL"
                                                    ? "active"
                                                    : ""
                                            }

                                            disabled={
                                                disabled
                                            }

                                            aria-pressed={
                                                quickPaymentPreset ===
                                                "FULL"
                                            }

                                            onClick={
                                                () =>
                                                    handleQuickPayment(
                                                        "FULL",
                                                        100
                                                    )
                                            }
                                        >
                                            Full Balance
                                        </button>


                                        <button
                                            type="button"

                                            className={
                                                quickPaymentPreset ===
                                                "HALF"
                                                    ? "active"
                                                    : ""
                                            }

                                            disabled={
                                                disabled ||
                                                calculated
                                                    .grandTotal <=
                                                0
                                            }

                                            aria-pressed={
                                                quickPaymentPreset ===
                                                "HALF"
                                            }

                                            onClick={
                                                () =>
                                                    handleQuickPayment(
                                                        "HALF",
                                                        50
                                                    )
                                            }
                                        >
                                            50%
                                        </button>


                                        <button
                                            type="button"

                                            className={
                                                quickPaymentPreset ===
                                                "QUARTER"
                                                    ? "active"
                                                    : ""
                                            }

                                            disabled={
                                                disabled ||
                                                calculated
                                                    .grandTotal <=
                                                0
                                            }

                                            aria-pressed={
                                                quickPaymentPreset ===
                                                "QUARTER"
                                            }

                                            onClick={
                                                () =>
                                                    handleQuickPayment(
                                                        "QUARTER",
                                                        25
                                                    )
                                            }
                                        >
                                            25%
                                        </button>

                                    </div>

                                </div>

                            </div>

                            {calculated
                                .paidAmount >
                                0 && (
                                <div className="invoice-totals-payment-success">
                                    <FiCheckCircle />

                                    Payment of{" "}
                                    {formatMoney(
                                        calculated
                                            .paidAmount
                                    )}{" "}
                                    will be included with this document.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <aside className="invoice-totals-summary-card">
                    <div className="invoice-totals-summary-row">
                        <span>
                            Item Gross
                        </span>

                        <strong>
                            {formatMoney(
                                calculated
                                    .itemGrossAmount
                            )}
                        </strong>
                    </div>

                    <div className="invoice-totals-summary-row invoice-totals-summary-negative">
                        <span>
                            Item Discounts
                        </span>

                        <strong>
                            -{" "}
                            {formatMoney(
                                calculated
                                    .itemDiscountAmount
                            )}
                        </strong>
                    </div>

                    <div className="invoice-totals-summary-row">
                        <span>
                            Subtotal
                        </span>

                        <strong>
                            {formatMoney(
                                calculated
                                    .subtotalAmount
                            )}
                        </strong>
                    </div>

                    <div className="invoice-totals-summary-row invoice-totals-summary-negative">
                        <span>
                            Invoice Discount
                        </span>

                        <strong>
                            -{" "}
                            {formatMoney(
                                calculated
                                    .invoiceDiscountAmount
                            )}
                        </strong>
                    </div>

                    <div className="invoice-totals-summary-divider" />

                    <div className="invoice-totals-summary-row">
                        <span>
                            Taxable Amount
                        </span>

                        <strong>
                            {formatMoney(
                                calculated
                                    .taxableAmount
                            )}
                        </strong>
                    </div>

                    {showTaxBreakup && (
                        <>
                            {calculated
                                .cgstAmount >
                                0 && (
                                <div className="invoice-totals-summary-row invoice-totals-summary-muted">
                                    <span>
                                        CGST
                                    </span>

                                    <strong>
                                        {formatMoney(
                                            calculated
                                                .cgstAmount
                                        )}
                                    </strong>
                                </div>
                            )}

                            {calculated
                                .sgstAmount >
                                0 && (
                                <div className="invoice-totals-summary-row invoice-totals-summary-muted">
                                    <span>
                                        SGST
                                    </span>

                                    <strong>
                                        {formatMoney(
                                            calculated
                                                .sgstAmount
                                        )}
                                    </strong>
                                </div>
                            )}

                            {calculated
                                .igstAmount >
                                0 && (
                                <div className="invoice-totals-summary-row invoice-totals-summary-muted">
                                    <span>
                                        IGST
                                    </span>

                                    <strong>
                                        {formatMoney(
                                            calculated
                                                .igstAmount
                                        )}
                                    </strong>
                                </div>
                            )}

                            {calculated
                                .cessAmount >
                                0 && (
                                <div className="invoice-totals-summary-row invoice-totals-summary-muted">
                                    <span>
                                        Cess
                                    </span>

                                    <strong>
                                        {formatMoney(
                                            calculated
                                                .cessAmount
                                        )}
                                    </strong>
                                </div>
                            )}
                        </>
                    )}

                    {calculated
                        .shippingCharge >
                        0 && (
                        <div className="invoice-totals-summary-row">
                            <span>
                                Shipping
                            </span>

                            <strong>
                                {formatMoney(
                                    calculated
                                        .shippingCharge
                                )}
                            </strong>
                        </div>
                    )}

                    {calculated
                        .packingCharge >
                        0 && (
                        <div className="invoice-totals-summary-row">
                            <span>
                                Packing
                            </span>

                            <strong>
                                {formatMoney(
                                    calculated
                                        .packingCharge
                                )}
                            </strong>
                        </div>
                    )}

                    {calculated
                        .otherCharge >
                        0 && (
                        <div className="invoice-totals-summary-row">
                            <span>
                                Other Charges
                            </span>

                            <strong>
                                {formatMoney(
                                    calculated
                                        .otherCharge
                                )}
                            </strong>
                        </div>
                    )}

                    {calculated
                        .roundOffAmount !==
                        0 && (
                        <div className="invoice-totals-summary-row invoice-totals-summary-muted">
                            <span>
                                Round Off
                            </span>

                            <strong>
                                {calculated
                                    .roundOffAmount >
                                0
                                    ? "+"
                                    : "-"}{" "}
                                {formatMoney(
                                    Math.abs(
                                        calculated
                                            .roundOffAmount
                                    )
                                )}
                            </strong>
                        </div>
                    )}

                    <div className="invoice-totals-summary-grand">
                        <span>
                            Grand Total
                        </span>

                        <strong>
                            {formatMoney(
                                calculated
                                    .grandTotal
                            )}
                        </strong>
                    </div>

                    {showPayment &&
                        !isQuotation && (
                        <>
                            <div className="invoice-totals-summary-row invoice-totals-summary-paid">
                                <span>
                                    Paid Amount
                                </span>

                                <strong>
                                    {formatMoney(
                                        calculated
                                            .paidAmount
                                    )}
                                </strong>
                            </div>

                            <div className="invoice-totals-summary-balance">
                                <span>
                                    Balance Due
                                </span>

                                <strong>
                                    {formatMoney(
                                        calculated
                                            .balanceAmount
                                    )}
                                </strong>
                            </div>
                        </>
                    )}

                    {Object.keys(
                        errors
                    ).length >
                        0 && (
                        <div className="invoice-totals-error-box">
                            <FiAlertCircle />

                            Check the highlighted total fields before saving.
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
};

export {
    EMPTY_INVOICE_TOTALS,
    calculateInvoiceTotals,
    validateInvoiceTotals,
    getPaymentStatus,
    formatMoney,
};

export default InvoiceTotals;